import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TableSkeleton,
  EmptyState,
  OrderTemplatesList,
  ConfirmDeleteModal,
  SuccessToast,
  ValidationToast,
  Button,
  emptyOrderIllustration,
} from "@components";
import { HiOutlinePlus } from "react-icons/hi";
import { useOrderTemplates } from "@data";
import { useCache } from "../../../context/CacheContext";
import { useTimedToast } from "../../../hooks";
import { OrderTemplate } from "../../../models";
import { textSecondary, textHeading } from "@design-system";
import { compareTemplatesNewestFirst } from "../../../helpers/orderTemplateSorting";
import { useOrderTemplatePermissions } from "./permissions";

export const OrderTemplatesPage: FC = () => {
  const { t } = useTranslation(["orders", "common"]);
  const navigate = useNavigate();
  const { updateCacheKey } = useCache();
  const { canManage, canCreateOrder } = useOrderTemplatePermissions();

  const templatesQuery = useOrderTemplates();
  const isLoading = templatesQuery.isLoading || templatesQuery.isRefetching;

  const [templateToDelete, setTemplateToDelete] =
    useState<OrderTemplate | null>(null);
  const {
    show: showToast,
    message: toastMessage,
    showToast: triggerToast,
    hideToast,
  } = useTimedToast(3000);
  const {
    show: showError,
    message: errorMessage,
    showToast: triggerError,
    hideToast: hideError,
  } = useTimedToast(4000);

  const [isSplitOpen, setIsSplitOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "recurring">("all");

  const allTemplates = useMemo(() => {
    const list = templatesQuery.data ?? [];
    return [...list].sort(compareTemplatesNewestFirst);
  }, [templatesQuery.data]);

  const templates = useMemo(
    () =>
      filter === "recurring"
        ? allTemplates.filter((template) => !!template.recurrence)
        : allTemplates,
    [allTemplates, filter],
  );

  const hasRecurringTemplates = useMemo(
    () => allTemplates.some((template) => !!template.recurrence),
    [allTemplates],
  );

  const handleNewTemplate = () => navigate("/order-templates/new");

  const handleToggleRecurrence = async (template: OrderTemplate) => {
    if (!template.recurrence) return;
    const active = !template.recurrence.active;
    try {
      await OrderTemplate.update(template, {
        recurrence: { ...template.recurrence, active },
      });
      updateCacheKey();
      triggerToast(
        t(active ? "templates.toast.resumed" : "templates.toast.paused", {
          name: template.name,
        }),
      );
    } catch (error) {
      console.error("Failed to toggle template recurrence", error);
      triggerError(t("templates.toast.toggle_failed", { name: template.name }));
    }
  };

  const handleEditTemplate = (template: OrderTemplate) =>
    navigate(`/order-templates/edit/${template.id}`);

  const handleCreateOrderWithTemplate = (template: OrderTemplate) =>
    navigate("/orders/new", { state: { templateId: template.id } });

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    const name = templateToDelete.name;
    try {
      await OrderTemplate.remove(templateToDelete);
      updateCacheKey();
      triggerToast(t("templates.toast.deleted", { name }));
      // Only dismiss the dialog once the delete actually succeeded.
      setTemplateToDelete(null);
    } catch (error) {
      // Keep the dialog open and tell the user, instead of silently closing it.
      console.error("Failed to delete order template", error);
      triggerError(t("templates.toast.delete_failed", { name }));
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="px-4 py-2">
          <TableSkeleton />
        </div>
      ) : allTemplates.length === 0 ? (
        <EmptyState
          icon={emptyOrderIllustration}
          title={t("templates.empty_state.title")}
          description={t("templates.empty_state.description")}
          buttonText={canManage ? t("templates.new_template") : undefined}
          buttonIcon={canManage ? "plus" : undefined}
          buttonClick={canManage ? handleNewTemplate : undefined}
        />
      ) : (
        <div className="space-y-4 px-4 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className={`text-2xl ${textHeading}`}>
                {t("templates.title")}
              </h1>
              <p className={textSecondary}>{t("templates.description")}</p>
            </div>
            {canManage && !isSplitOpen && (
              <Button
                variant="primary"
                icon={<HiOutlinePlus />}
                onClick={handleNewTemplate}
                className="w-full sm:w-auto"
              >
                {t("templates.new_template")}
              </Button>
            )}
          </div>

          {hasRecurringTemplates && (
            <div
              className="inline-flex rounded-lg border border-border p-0.5"
              role="tablist"
            >
              {(["all", "recurring"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={filter === value}
                  onClick={() => setFilter(value)}
                  className={
                    "px-3 py-1.5 text-sm font-medium rounded-md transition-colors " +
                    (filter === value
                      ? "bg-brand text-white"
                      : "text-secondary hover:bg-surface-muted")
                  }
                >
                  {t(`templates.filter_${value}`)}
                </button>
              ))}
            </div>
          )}

          <OrderTemplatesList
            templates={templates}
            isOpen={setIsSplitOpen}
            canManage={canManage}
            canCreateOrder={canCreateOrder}
            onEdit={handleEditTemplate}
            onDelete={setTemplateToDelete}
            onCreateOrder={handleCreateOrderWithTemplate}
            onToggleRecurrence={handleToggleRecurrence}
          />
        </div>
      )}

      <ConfirmDeleteModal
        show={!!templateToDelete}
        title={t("templates.delete_dialog.title")}
        description={t("templates.delete_dialog.description")}
        highlightedName={templateToDelete?.name ?? ""}
        cancelLabel={t("common:cancel")}
        confirmLabel={t("common:delete")}
        onClose={() => setTemplateToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <SuccessToast
        title={t("common:success")}
        message={toastMessage}
        show={showToast}
        onClose={hideToast}
        position="top-right"
      />

      <ValidationToast
        title={t("common:error")}
        message={errorMessage}
        show={showError}
        onClose={hideError}
        position="top-right"
      />
    </>
  );
};

export default OrderTemplatesPage;
